import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import moment from 'moment';

import StandardDialog from '../Modals/StandardDialog';
import { DataCell, DataRow } from '../Tables/DataList';
import { getBillingRecordHistory } from '../../services/billingRecords';
import { BillingRecordHistoryEntry } from '../../interfaces/billing.interface';
import { humanizeField, parseImportedName, humanizeValue } from './billingRecordHistoryUtils';

interface BillingRecordHistoryModalProps {
  open: boolean;
  onClose: () => void;
  billingRecordId: number;
  companyName: string;
}

interface HistoryRow {
  key: string;
  actor: string;
  date: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  isImported: boolean;
}

export default function BillingRecordHistoryModal({ open, onClose, billingRecordId, companyName }: BillingRecordHistoryModalProps) {
  const [entries, setEntries] = useState<BillingRecordHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedField, setSelectedField] = useState<string>('all');

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(false);
    setEntries([]);
    setSelectedField('all');
    getBillingRecordHistory(billingRecordId)
      .then((data) => { if (active) setEntries(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, billingRecordId]);

  const availableFields = useMemo(() => {
    const fields = new Set<string>();
    entries.forEach((entry) => entry.changes.forEach((change) => fields.add(change.field)));
    return Array.from(fields).sort((a, b) => humanizeField(a).localeCompare(humanizeField(b)));
  }, [entries]);

  // One row per change: an entry that changed two fields becomes two rows sharing user + date.
  const rows = useMemo<HistoryRow[]>(() => {
    const flattened: HistoryRow[] = [];
    entries.forEach((entry) => {
      const isImported = !entry.historyUser && !!entry.historyChangeReason?.startsWith('Imported from Monday');
      const actor = isImported
        ? (parseImportedName(entry.historyChangeReason) ?? 'Finance Tracker')
        : (entry.historyUser ?? 'System');
      const date = moment(entry.historyDate).format('MM/DD/YYYY HH:mm');
      entry.changes
        .filter((change) => selectedField === 'all' || change.field === selectedField)
        // Skip no-visible-change rows (e.g. null -> '' both render as —).
        .filter((change) => humanizeValue(change.old) !== humanizeValue(change.new))
        .forEach((change) => flattened.push({
          key: `${entry.historyId}-${change.field}`,
          actor,
          date,
          field: change.field,
          oldValue: change.old,
          newValue: change.new,
          isImported,
        }));
    });
    return flattened;
  }, [entries, selectedField]);

  return (
    <StandardDialog title="Billing Record History" subtitle={companyName} isOpen={open} handleClose={onClose} maxWidth="lg">
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="invoice-history-modal-field-filter-label">Filter by field</InputLabel>
        <Select
          labelId="invoice-history-modal-field-filter-label"
          label="Filter by field"
          value={selectedField}
          onChange={(e) => setSelectedField(e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          {availableFields.map((field) => (
            <MenuItem key={field} value={field}>{humanizeField(field)}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" variant="body2">Unable to load the change history. Please try again.</Typography>
      )}

      {!loading && !error && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">No change history to display.</Typography>
      )}

      {!loading && !error && rows.length > 0 && (
        <Box sx={{ width: '100%', maxHeight: '750px', overflow: 'auto' }}>
          <DataRow header>
            <DataCell xs={4}>User</DataCell>
            <DataCell xs={3}>Date</DataCell>
            <DataCell xs={2}>Field</DataCell>
            <DataCell xs={3}>Change</DataCell>
          </DataRow>
          {rows.map((row) => (
            <DataRow key={row.key}>
              <DataCell xs={4} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                  <Box component="span" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{row.actor}</Box>
                  {row.isImported && (
                    <Chip size="small" color="warning" variant="outlined" label="via Finance Tracker" />
                  )}
                </Box>
              </DataCell>
              <DataCell xs={3}>{row.date}</DataCell>
              <DataCell xs={2}>{humanizeField(row.field)}</DataCell>
              <DataCell xs={3} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{humanizeValue(row.oldValue)} → {humanizeValue(row.newValue)}</DataCell>
            </DataRow>
          ))}
        </Box>
      )}
    </StandardDialog>
  );
}
